import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Search,
  Bell,
  TrendingUp,
  User,
  PlusSquare,
  Settings,
  Users,
  Edit,
} from "lucide-react";
import FriendsModal from "../components/Modal/FriendsModal";
import { useNotification } from "../context/NotificationContext";
import { useAuth } from "../context/AuthContext";

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotificationDot } = useNotification();
  const [friendsModalOpen, setFriendsModalOpen] = useState(false);
  const { user: loggedInUser } = useAuth();

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [sidebarOpen]);

  const getInitials = (name = "User") => {
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const handleMenuItemClick = (path, itemLabel) => {
    if (itemLabel === "Friends") {
      setFriendsModalOpen(true);
      setSidebarOpen(false);
      return;
    }
    if (itemLabel === "Notifications") {
      navigate("/notifications");
      setSidebarOpen(false);
      return;
    }
    navigate(path);
    setSidebarOpen(false);
  };

  const menuItems = [
    { icon: Home, label: "Home", path: "/home" },
    { icon: Search, label: "Explore", path: "/explore" },
    { icon: TrendingUp, label: "Trending", path: "/trending" },
    { icon: Bell, label: "Notifications", path: "/notifications" },
    { icon: PlusSquare, label: "Create Post", path: "/create-post" },
    { icon: Users, label: "Friends", path: "/friends" },
    {
      icon: User,
      label: "Profile",
      path: `/${loggedInUser?.profile?.username || "profile"}`,
    },
    { icon: Settings, label: "Settings", path: "/settings" },
  ];

  const isActive = (path, label) => {
    if (label === "Friends" && friendsModalOpen) return true;
    const currentPath = location?.pathname?.toLowerCase();

    if (label === "Profile") {
      const usernamePath = `/${loggedInUser?.profile?.username?.toLowerCase()}`;
      return currentPath === usernamePath;
    }

    if (path === "/home") {
      return currentPath === "/" || currentPath === "/home";
    }
    return currentPath.startsWith(path.toLowerCase());
  };

  // Helper component to render Profile Icon/Picture logic
  const ProfileImage = ({ active }) => {
    const profilePic = loggedInUser?.profile?.profilePicture;
    const profilePictureUrl =
      profilePic && typeof profilePic === "object"
        ? profilePic.small || profilePic.medium || profilePic.thumbnail
        : profilePic;

    return (
      <div
        className={`w-6 h-6 rounded-full overflow-hidden border transition-all flex-shrink-0 ${
          active ? "border-black ring-gray-300" : "border-gray-300"
        }`}
      >
        {profilePictureUrl ? (
          <img
            src={profilePictureUrl}
            className="w-full h-full object-cover"
            alt="Profile"
          />
        ) : loggedInUser?.profile?.name ? (
          <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
            <span className="text-slate-600 font-bold text-[8px]">
              {getInitials(loggedInUser.profile.name)}
            </span>
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
            <svg
              className="w-full h-full text-slate-400"
              viewBox="0 0 100 100"
              fill="currentColor"
            >
              <circle cx="50" cy="35" r="18" />
              <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
            </svg>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-40 min-[1285px]:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`fixed left-0 top-0 h-full bg-white z-50 flex flex-col transform transition-transform duration-300 ease-in-out min-[1285px]:hidden ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ width: "80px" }}
      >
        <div className="px-4 pt-4 flex justify-center">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center justify-center"
          >
            <img 
              src="/logos/Wryven-Feather-Gradient.png?v=1.0" 
              alt="Wryven Logo"
              className="w-12 h-12 rounded-lg object-cover"
              loading="eager"
            />
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1">
          {menuItems.map((item, index) => {
            const active = isActive(item.path, item.label);
            return (
              <button
                key={index}
                onClick={() => handleMenuItemClick(item.path, item.label)}
                className={`w-full flex flex-col items-center gap-1 px-2 py-3 rounded-lg relative transition-all ${
                  active ? "text-black" : "text-black hover:bg-neutral-100"
                }`}
              >
                {active && (
                  <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-black rounded-r-full" />
                )}
                <div className="relative">
                  {item.label === "Profile" ? (
                    <ProfileImage active={active} />
                  ) : (
                    <item.icon
                      className="w-6 h-6"
                      strokeWidth={active ? 2.5 : 1.5}
                    />
                  )}
                  {item.label === "Notifications" && showNotificationDot && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                  )}
                </div>
                <span
                  className={`text-[10px] font-medium text-center ${
                    active ? "text-black" : "text-gray-600"
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Edit Profile at Bottom */}
        <div className="px-2 py-2 border-t border-neutral-200">
          <button
            onClick={() => handleMenuItemClick("/edit-profile")}
            className={`w-full flex flex-col items-center gap-1 px-2 py-1 rounded-lg transition-all duration-200 ${
              isActive("/edit-profile")
                ? "text-black"
                : "text-black hover:bg-neutral-100"
            }`}
          >
            <Edit className="w-6 h-6" strokeWidth={1.5} />
            <span
              className={`text-[10px] font-medium text-center ${
                isActive("/edit-profile") ? "text-black" : "text-gray-600"
              }`}
            >
              Edit
            </span>
          </button>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden min-[1285px]:flex fixed left-0 top-0 h-full w-[245px] bg-white border-r border-neutral-200 z-50 flex-col">
        <div className="px-6 py-5">
          <button
            onClick={() => navigate("/home")}
            className="wryven-font text-5xl text-black tracking-tight"
          >
            Wryven
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {menuItems.map((item, index) => {
            const active = isActive(item.path, item.label);
            return (
              <button
                key={index}
                onClick={() => handleMenuItemClick(item.path, item.label)}
                className={`w-full flex items-center gap-4 px-3 py-3 rounded-lg transition-all relative ${
                  active
                    ? "font-bold text-black"
                    : "text-black hover:bg-neutral-100"
                }`}
              >
                {active && (
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-10 bg-black rounded-r-full" />
                )}
                <div className="relative">
                  {item.label === "Profile" ? (
                    <ProfileImage active={active} />
                  ) : (
                    <item.icon
                      className="w-6 h-6"
                      strokeWidth={active ? 2.5 : 1.5}
                    />
                  )}
                  {item.label === "Notifications" && showNotificationDot && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                  )}
                </div>
                <span className="text-[15px]">{item.label}</span>
              </button>
            );
          })}
        </nav>
        {/* Edit Profile at Bottom */}

        <div className="px-3 py-4 border-t border-neutral-200">
          <button
            onClick={() => handleMenuItemClick("/edit-profile")}
            className={`w-full flex items-center gap-4 px-3 py-3 rounded-lg transition-all duration-200 ${
              isActive("/edit-profile")
                ? "font-semibold"
                : "text-black hover:bg-neutral-100"
            }`}
          >
            <Edit className="w-6 h-6" strokeWidth={1.5} />

            <span className="font-normal text-[15px]">Edit Profile</span>
          </button>
        </div>
      </div>

      <FriendsModal
        open={friendsModalOpen}
        onClose={() => setFriendsModalOpen(false)}
      />
    </>
  );
};

export default Sidebar;
